import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Brand } from "../Brand";
import api from "../../lib/api";
import {
  ArrowRight, Zap, ShieldCheck, Landmark, Globe2, Clock3, Lock,
  BadgeCheck, CreditCard, Banknote, ChevronRight,
} from "lucide-react";

/** Premium marketing page for the Buy Crypto product — /buy-crypto */
export default function BuyCryptoLanding() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/buy/config").then((r) => setCfg(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const methods = cfg?.methods || [];
  const assets = cfg?.assets || [];

  return (
    <div className="min-h-screen bg-background" data-testid="buy-crypto-landing">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="buyland-logo-btn">
            <Brand size={26} />
          </button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => navigate("/")}
              data-testid="buyland-home-btn">Home</Button>
            <Button size="sm" className="rounded-xl gap-1.5" onClick={() => navigate("/buy")}
              data-testid="buyland-cta-top">
              Start Buying <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="max-w-2xl">
            <div className="xch-eyebrow mb-4" data-testid="buyland-eyebrow">New — Buy Crypto</div>
            <h1 className="xch-headline text-4xl sm:text-5xl lg:text-6xl" data-testid="buyland-headline">
              Buy crypto in minutes.<br />
              <span className="text-primary">Bank-grade rails.</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-xl">
              Purchase Bitcoin, Ethereum, stablecoins and more with PayID, Interac
              e-Transfer or card — settled into your Xchangely account on secure,
              compliant infrastructure.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button size="lg" className="rounded-xl gap-2 px-7" onClick={() => navigate("/buy")}
                data-testid="buyland-cta-hero">
                Buy Crypto <ArrowRight className="w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-xl" onClick={() => navigate("/register")}
                data-testid="buyland-cta-signup">Create free account</Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-primary" /> KYC-verified checkout</span>
              <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-primary" /> Keys never leave our vault</span>
              <span className="inline-flex items-center gap-1.5"><Clock3 className="w-3.5 h-3.5 text-primary" /> Live status tracking</span>
            </div>
          </div>
        </div>
      </section>

      {/* Payment methods */}
      <section className="border-t border-border bg-secondary/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <div className="xch-eyebrow mb-3">Payment methods</div>
          <h2 className="font-display font-bold text-base md:text-lg mb-8">Local rails, global reach</h2>
          <div className="grid sm:grid-cols-3 gap-4" data-testid="buyland-methods">
            {(methods.length ? methods : [
              { key: "payid", label: "PayID", region: "Australia", settlement: "T+3 settlement" },
              { key: "interac", label: "Interac e-Transfer", region: "Canada", settlement: "T+3 settlement" },
              { key: "card", label: "Debit / Credit Card", region: "Global", settlement: "Instant capture" },
            ]).map((m) => (
              <div key={m.key} className="xch-card p-6" data-testid={`buyland-method-${m.key}`}>
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  {m.key === "card" ? <CreditCard className="w-5 h-5 text-primary" /> : <Banknote className="w-5 h-5 text-primary" />}
                </div>
                <div className="font-display font-semibold">{m.label}</div>
                <div className="text-sm text-muted-foreground mt-1">{m.region}</div>
                <div className="text-xs text-muted-foreground mt-3 inline-flex items-center gap-1.5">
                  <BadgeCheck className="w-3.5 h-3.5 text-emerald-600" /> {m.settlement}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Assets */}
      <section>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <div className="xch-eyebrow mb-3">Supported assets</div>
          <h2 className="font-display font-bold text-base md:text-lg mb-8">10 assets across 8 networks</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="buyland-assets">
            {assets.map((a) => (
              <button key={a.key} onClick={() => navigate(`/buy?asset=${a.key}`)}
                className="xch-card p-4 text-left hover:border-primary/40 transition-colors group"
                data-testid={`buyland-asset-${a.key}`}>
                <div className="font-display font-semibold text-sm flex items-center justify-between">
                  {a.symbol}
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{a.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mt-2">{a.network}</div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="border-t border-border bg-secondary/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <div className="xch-eyebrow mb-3">Why Xchangely</div>
          <h2 className="font-display font-bold text-base md:text-lg mb-8">Built like a payment institution</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Zap, t: "Fast settlement", d: "Sessions confirm in real time; assets credit as soon as the network settles." },
              { icon: ShieldCheck, t: "Compliant by design", d: "KYC/AML handled inside the secure provider checkout on every purchase." },
              { icon: Landmark, t: "Institutional custody", d: "Purchases settle to segregated, monitored treasury wallets per asset." },
              { icon: Globe2, t: "Local payment rails", d: "PayID in Australia, Interac in Canada, cards worldwide — more coming." },
            ].map((f) => (
              <div key={f.t} className="xch-card p-6">
                <f.icon className="w-5 h-5 text-primary mb-3" />
                <div className="font-display font-semibold text-sm">{f.t}</div>
                <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 text-center">
          <h2 className="font-display font-bold text-2xl sm:text-3xl">Ready to make your first purchase?</h2>
          <p className="text-muted-foreground mt-2 text-sm">No hidden fees. Final rate confirmed before you pay.</p>
          <Button size="lg" className="rounded-xl gap-2 px-8 mt-6" onClick={() => navigate("/buy")}
            data-testid="buyland-cta-final">
            Get Started <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground">
        Crypto purchases are processed by our licensed payment partners. Digital assets are volatile — only invest what you can afford to lose.
      </footer>
    </div>
  );
}
